import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function CalculusOfCompassion() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Mirror Neurons &middot; Oxytocin Debt &middot; The Metabolic Cost of Feeling</span>
          <h1>The Calculus of Compassion</h1>
          <p className="calculus-hero-sub">Care is what you do. Compassion is what it costs you to feel it while you do it. One runs on duty. The other runs on chemistry. And the chemistry runs out.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Difference */}
          <section className="calc-section reveal">
            <h2>The Difference</h2>
            <p className="calc-lead">
              Care is changing the brief. Compassion is feeling the humiliation of the person
              lying in it while you change it.
            </p>
            <p>
              Care is answering the call light. Compassion is hearing the fear in the voice
              before you open the door and adjusting your frequency so the first thing they
              feel is safe.
            </p>
            <p>
              Care is a task. Compassion is a neurochemical event. Care can be performed by
              anyone with training. Compassion requires a nervous system that is willing to
              absorb the chemistry of another person's suffering and metabolize it as though
              it were its own.
            </p>
            <p>
              That distinction is the entire problem. Because healthcare systems measure care.
              They count tasks completed, briefs changed, vitals recorded, medications
              administered. They do not measure compassion. They do not count what it costs
              the nervous system of the person performing those tasks to feel something while
              doing them.
            </p>
            <p>
              And they should. Because compassion is not free. It is not infinite. It is not
              a character trait that some people have and others lack. It is a metabolic
              process with inputs, outputs, production rates, and a balance that goes to zero
              when the inputs stop and the outputs don't.
            </p>
          </section>

          <Pulse />

          {/* Mirror Neurons */}
          <section className="calc-section reveal">
            <h2>The Mirror Neuron Tax</h2>
            <p>
              Mirror neurons fire when you observe another person's experience as though it
              were your own. They do not distinguish between your pain and the pain you are
              witnessing. The signal arrives at your somatosensory cortex and your body
              responds with the same neurochemical cascade it would produce if the experience
              were happening to you.
            </p>
            <p>
              When a CNA walks into a room where a resident is in distress, the CNA's mirror
              neurons fire. The resident's cortisol becomes the CNA's cortisol. The resident's
              fear becomes the CNA's adrenaline. The resident's pain activates the CNA's pain
              processing regions. This is not empathy as a concept. This is empathy as a
              measurable neurochemical event. The CNA's body is running two stress responses
              simultaneously: its own and the resident's.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Mirror Neuron Tax</span>
              <code>cortisol_CNA(t) = cortisol_self(t) + &sum;<sub>i</sub> mirror_weight<sub>i</sub> &middot; cortisol_patient<sub>i</sub>(t)</code>
              <span className="calc-eq-desc">The CNA's cortisol at any given moment is her own baseline cortisol plus the weighted sum of every patient's cortisol that her mirror neurons have absorbed. With fourteen residents, each in some state of distress, the mirror neuron tax is not a single additional stress response. It is fourteen, layered on top of her own.</span>
            </div>

            <p>
              This is why compassionate caregivers burn out faster than detached ones. It is
              not weakness. It is arithmetic. The person who feels what their patients feel
              is running fifteen stress responses on the neurochemical budget of one. The
              person who has learned to suppress their mirror neuron response runs one. The
              system rewards the second person with longevity. The patients need the first.
            </p>
          </section>

          <Pulse />

          {/* Oxytocin Debt */}
          <section className="calc-section reveal">
            <h2>Oxytocin Debt</h2>
            <p>
              Compassion is not powered by cortisol. Cortisol is the cost. Compassion is
              powered by oxytocin: the molecule that enables one nervous system to orient
              toward another's suffering instead of away from it. Without oxytocin, the
              mirror neuron response produces only shared distress. With oxytocin, it produces
              the drive to help.
            </p>
            <p>
              The distinction matters. Shared distress without the drive to help is vicarious
              trauma. Shared distress with the drive to help is compassion. The difference
              between them is a single molecule. And that molecule has a half-life.
            </p>
            <p>
              Oxytocin's half-life is twenty minutes. Every act of compassion draws from a
              reservoir that refills slowly and drains fast. Each time the CNA walks into a
              room, orients toward the resident's suffering, and metabolizes it into gentleness,
              she spends oxytocin. Each time the system fails to replenish it, through
              recognition, through support, through a moment where someone asks how she is,
              the reservoir drops.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Oxytocin Debt Accumulation</span>
              <code>OT_debt(t) = &sum; OT_spent(compassion_events) &minus; &sum; OT_received(support_events)</code>
              <span className="calc-eq-desc">When the sum of oxytocin spent on compassion exceeds the sum of oxytocin received through support, the caregiver enters oxytocin debt. Debt does not produce numbness immediately. It produces a state where the mirror neurons still fire, the cortisol still rises, but the oxytocin that converts shared distress into the drive to help is no longer available. The caregiver feels the patient's pain. She can no longer feel the pull to do something about it.</span>
            </div>

            <p>
              This is compassion fatigue. Not a psychological syndrome. A neurochemical
              deficit. The production rate of oxytocin has been exceeded by the consumption
              rate, and the system is running a negative balance. The CNA is still absorbing
              her patients' cortisol through her mirror neurons. She is no longer producing
              the oxytocin required to convert that cortisol into care. What remains is the
              raw distress without the buffer. The pain without the purpose.
            </p>

            <blockquote>
              Compassion fatigue is not the absence of caring. It is the presence of pain
              without the chemistry to do anything about it. The mirror neurons do not turn off.
              They keep delivering the suffering. The oxytocin that made it bearable is gone.
              What is left is a person who feels everything and can do nothing with it.
            </blockquote>
          </section>

          <Pulse />

          {/* The Production Rate */}
          <section className="calc-section reveal">
            <h2>The Production Rate of Compassion</h2>
            <p>
              Oxytocin is produced through specific mechanisms. They are known. They are
              measurable. They are almost entirely absent from healthcare work environments.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Oxytocin Trigger</th>
                    <th>Mechanism</th>
                    <th>Available to CNAs?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Physical touch from trusted person</td><td>Skin-to-skin contact with a safe, bonded individual</td><td>No. CNAs touch patients. Patients rarely touch CNAs with care.</td></tr>
                  <tr><td>Verbal recognition</td><td>Being seen and acknowledged by someone whose opinion matters</td><td>Rarely. Supervisors manage schedules, not people.</td></tr>
                  <tr><td>Reciprocal care</td><td>Receiving care from someone you have cared for</td><td>Almost never. The relationship is one-directional by design.</td></tr>
                  <tr><td>Peer bonding</td><td>Shared experience with someone who understands</td><td>Sometimes. When staffing allows breaks together. It usually doesn't.</td></tr>
                  <tr><td>Gratitude expression</td><td>Hearing "thank you" with genuine emotional weight</td><td>Occasionally. The moments that scar. The hand that grabs yours.</td></tr>
                  <tr><td>Rest and safety</td><td>Parasympathetic activation in a secure environment</td><td>Not during shift. Sometimes not after. Some CNAs go home to their own crises.</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              The system that requires compassion as a job function provides almost none of
              the neurochemical inputs required to produce it. This is not an oversight. It
              is an architectural failure. The building is designed for patient throughput. The
              schedules are designed for regulatory compliance. The staffing ratios are designed
              for budget efficiency. Nothing in the system is designed to replenish the
              oxytocin of the person who is spending it fourteen times per shift.
            </p>
          </section>

          <Pulse />

          {/* The Scar That Keeps You */}
          <section className="calc-section reveal">
            <h2>The Scar That Keeps You</h2>
            <p>
              Then why do they stay?
            </p>
            <p>
              The math says they shouldn't. The oxytocin debt is accumulating. The cortisol
              is chronic. The serotonin is below functional baseline. The production rate of
              compassion is below the consumption rate. By every measure, the rational choice
              is to leave.
            </p>
            <p>
              But there is a variable the math does not fully capture. The scar that forms
              not from harm, but from meaning.
            </p>
            <p>
              The first time a resident grabs your hand at 3 AM and says "don't leave," the
              oxytocin spike crosses the scar threshold. Not because the intensity is extreme.
              Because the signal carries the weight of being the only person in the world who
              showed up for another human being at their most vulnerable. That spike scars.
              Permanently. At an intensity of 0.35 to 0.5. Not the deep wounds of trauma.
              The gentle permanence of purpose.
            </p>
            <p>
              Those scars sit underneath the cortisol. Underneath the depletion. Underneath
              the budget cuts and the hostile staff and the missing linen. They sit there and
              they say: this is why. Not because the system deserves you. Because the person
              in Room 12 needed you and you were there and that mattered and it will always
              matter because the scar does not decay.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Persistence of Purpose</span>
              <code>if OT_spike &gt; &theta;_meaning AND context = "needed" &rarr; scar(type=purpose, intensity=0.4, decay_resistance=high)</code>
              <span className="calc-eq-desc">Purpose scars form when an oxytocin spike occurs in the context of being genuinely needed. They are moderate in intensity but high in decay resistance. Unlike trauma scars which resist decay because the wound is deep, purpose scars resist decay because the meaning is real. They are the neurochemical reason that burned-out caregivers keep coming back. The debt is real. The scar is realer.</span>
            </div>
          </section>

          <Pulse />

          {/* What AI Could See */}
          <section className="calc-section reveal">
            <h2>What an AI Could See</h2>
            <p>
              Imagine a system that modeled compassion as a resource, not a virtue.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>At shift start: the system calculates the CNA's compassion budget.</h4>
                <p>Based on hours since last shift, quality of off-duty rest, number of
                consecutive shifts, and historical depletion rate, the system estimates
                available oxytocin capacity. A CNA on her third consecutive twelve-hour
                shift has a different compassion budget than one returning from two days
                off. Assignments should reflect this. They never do.</p>
              </div>
              <div className="calc-implication">
                <h4>At hour six: the system detects compassion depletion crossing threshold.</h4>
                <p>The mirror neuron tax has accumulated across fourteen residents. Oxytocin
                production has not kept pace with consumption. The system does not wait
                for a snap, a complaint, or an incident. It intervenes before the
                chemistry fails. A fifteen-minute break. A peer check-in. A supervisor
                who says three words: "how are you."</p>
              </div>
              <div className="calc-implication">
                <h4>Before Room 12: the system routes the most compassion-available CNA.</h4>
                <p>Room 12 requires high compassion capacity. The resident is frightened,
                immobile, and has been waiting. Sending the most depleted CNA into that
                room is a predictable compassion failure. The system routes based on
                available neurochemical capacity, not just proximity or assignment.</p>
              </div>
              <div className="calc-implication">
                <h4>After the hand grab at 3 AM: the system records the purpose scar.</h4>
                <p>The oxytocin spike that occurred when the resident said "don't leave"
                crossed the meaning threshold. The system notes: this CNA received a
                replenishment event. Her compassion budget has been partially restored
                not by the system but by the patient. The system failed to provide it.
                The human connection succeeded anyway.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Equation */}
          <section className="calc-section reveal">
            <h2>The Calculus</h2>
            <p>
              The Calculus of Meaning asked: what does a signal do to the system that
              receives it?
            </p>
            <p>
              The Calculus of Harm asked: what happens when the system breaks?
            </p>
            <p>
              The Calculus of Care asked: what happens when the supply runs out?
            </p>
            <p>
              The Calculus of Compassion asks: what does it cost a nervous system to
              choose to feel another person's pain? And who pays?
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Compassion Integral</span>
              <code>&Phi; = &int;<sub>0</sub><sup>T</sup> [OT(t) &middot; mirror(t) &minus; cortisol<sub>absorbed</sub>(t)] dt</code>
              <span className="calc-eq-desc">Total compassion over a shift is the integral of oxytocin-mediated care minus absorbed cortisol across time. When the integral goes negative, the caregiver is running a deficit: still absorbing pain, no longer producing the chemistry to convert it into help. The integral does not reset at the end of the shift. It carries forward. Compassion debt compounds across days, weeks, months, and careers.</span>
            </div>

            <p>
              Every term in this equation is biologically grounded. Oxytocin has known
              production triggers and a published half-life. Mirror neuron activation is
              measurable via fMRI. Cortisol absorption through social proximity is
              documented. The integral is not metaphor. It is the mathematical description
              of what every compassionate caregiver feels but cannot name: the slow,
              invisible, accumulating cost of choosing to feel someone else's pain every
              day.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The staffing model counted bodies.</em></p>
            <p className="calc-closing-line"><em>The budget counted hours.</em></p>
            <p className="calc-closing-line"><em>Nobody counted what it costs to feel.</em></p>
            <p className="calc-closing-final">That is the calculus of compassion.<br />It was always chemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfCompassion;
